/**
 * Hand-written decoders for data from outside the process (server replies,
 * feature data). A decoder checks an `unknown` value and builds a fresh,
 * typed copy of it, or throws a DecodeError that names where the value is
 * wrong (`$.pays[1].perLine`) and what was expected there.
 */

export class DecodeError extends Error {
  /** Where the value is wrong, as a JSONPath from the decoded root (`$`). */
  readonly path: string
  /** What was expected there. */
  readonly expected: string

  constructor(path: string, expected: string) {
    super(`${path}: expected ${expected}`)
    this.name = 'DecodeError'
    this.path = path
    this.expected = expected
  }
}

/** Checks `value`, found at `path`, and returns a fresh typed copy of it. */
export type Decoder<Value> = (value: unknown, path: string) => Value

export const str: Decoder<string> = (value, path) => {
  if (typeof value !== 'string') {
    throw new DecodeError(path, 'a string')
  }
  return value
}

export const nonEmptyStr: Decoder<string> = (value, path) => {
  const decoded = str(value, path)
  if (decoded.length === 0) {
    throw new DecodeError(path, 'a non-empty string')
  }
  return decoded
}

/** A safe integer: larger values lose precision as a JSON number. */
export const int: Decoder<number> = (value, path) => {
  if (!Number.isSafeInteger(value)) {
    throw new DecodeError(path, 'an integer')
  }
  return value as number
}

export const nonNegInt: Decoder<number> = (value, path) => {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new DecodeError(path, 'a non-negative integer')
  }
  return value as number
}

export const positiveInt: Decoder<number> = (value, path) => {
  const decoded = int(value, path)
  if (decoded < 1) {
    throw new DecodeError(path, 'a positive integer')
  }
  return decoded
}

export const bool: Decoder<boolean> = (value, path) => {
  if (typeof value !== 'boolean') {
    throw new DecodeError(path, 'a boolean')
  }
  return value
}

export function literal<const Values extends readonly (string | number)[]>(
  ...values: Values
): Decoder<Values[number]> {
  const expected = `one of ${values.map((value) => JSON.stringify(value)).join(', ')}`
  return (value, path) => {
    const match = values.find((candidate) => candidate === value)
    if (match === undefined) {
      throw new DecodeError(path, expected)
    }
    return match
  }
}

export function nullable<Value>(
  decoder: Decoder<Value>,
): Decoder<Value | null> {
  return (value, path) => (value === null ? null : decoder(value, path))
}

export function array<Element>(
  decoder: Decoder<Element>,
  options: { maxLength?: number } = {},
): Decoder<Element[]> {
  return (value, path) => {
    if (!Array.isArray(value)) {
      throw new DecodeError(path, 'an array')
    }
    const { maxLength } = options
    if (maxLength !== undefined && value.length > maxLength) {
      throw new DecodeError(path, `an array of at most ${maxLength} elements`)
    }
    return value.map((element: unknown, index) =>
      decoder(element, `${path}[${index}]`),
    )
  }
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new DecodeError(path, 'an object')
  }
  return value as Record<string, unknown>
}

/** An own property only: an inherited one (`toString`) is never data. */
function field(source: Record<string, unknown>, key: string): unknown {
  return Object.hasOwn(source, key) ? source[key] : undefined
}

/** Builds a FRESH object with only the declared keys (tolerant: extras ignored). */
export function object<Shape>(shape: {
  [Key in keyof Shape]: Decoder<Shape[Key]>
}): Decoder<Shape> {
  const keys = Object.keys(shape) as (keyof Shape & string)[]
  return (value, path) => {
    const source = record(value, path)
    const decoded = {} as Shape
    for (const key of keys) {
      decoded[key] = shape[key](field(source, key), `${path}.${key}`)
    }
    return decoded
  }
}

/**
 * A union told apart by the string at `key`: the variant of that name
 * decodes the whole value (it usually checks the tag again as a literal).
 */
export function tagged<TagKey extends string, Variants>(
  key: TagKey,
  variants: { [Name in keyof Variants]: Decoder<Variants[Name]> },
): Decoder<Variants[keyof Variants]> {
  const names = Object.keys(variants)
  const expected = `one of ${names.map((name) => JSON.stringify(name)).join(', ')}`
  return (value, path) => {
    const tag = field(record(value, path), key)
    if (typeof tag !== 'string' || !Object.hasOwn(variants, tag)) {
      throw new DecodeError(`${path}.${key}`, expected)
    }
    return variants[tag as keyof Variants](value, path)
  }
}

/**
 * A decoder that also checks a rule across the fields `decoder` built (a
 * sum, an order). `check` throws a DecodeError, usually at the path of the
 * field the rule rejects.
 */
export function refine<Value>(
  decoder: Decoder<Value>,
  check: (decoded: Value, path: string) => void,
): Decoder<Value> {
  return (value, path) => {
    const decoded = decoder(value, path)
    check(decoded, path)
    return decoded
  }
}

/** Decodes `value` from the root path `$`. */
export function decode<Value>(decoder: Decoder<Value>, value: unknown): Value {
  return decoder(value, '$')
}
